import type { ContentType, ScreenTimeBudget } from '@kidshub/contracts';
import { computeRemaining, type LimitingFactor } from '@kidshub/policy';

/**
 * How much time is left, between two polls.
 *
 * The dashboard is a picture, and a picture a few seconds behind is not wrong —
 * which is why it polls rather than holding a stream open. But a number that
 * only moves every ten seconds looks broken, so the seconds in between are
 * projected forward locally.
 *
 * **The projection goes through `computeRemaining` from `@kidshub/policy`.**
 * That is the same function the gate uses to decide whether the tablet may keep
 * playing. Hand-rolling `remaining - elapsed` here would be a second piece of
 * budget arithmetic in a third place, and the whole reason the policy package
 * exists is that two implementations drift invisibly.
 *
 * **And it is clamped to what the server last said, minus the time since.** The
 * dashboard cannot see everything the gate can: `LiveDeviceState` carries the
 * total spent today but not the split per content type, and it carries no
 * schedule window. If either of those is the binding limit, the local
 * computation would be generous. Clamping means this screen can be a little
 * pessimistic and never optimistic — and being optimistic is the failure that
 * matters, because it is the one where a parent reads eleven minutes and the
 * tablet stops at four.
 */

export interface CountdownInput {
  /** What the server reported, at the moment it reported it. */
  serverRemainingSeconds: number;
  /** Which limit the server's figure came from. */
  serverLimitingFactor: LimitingFactor;
  /** Seconds elapsed since that response arrived. */
  secondsSincePoll: number;
  /** Nothing is spent while no session is running. */
  isSessionRunning: boolean;
  budget: ScreenTimeBudget;
  contentType: ContentType;
  spentTodaySeconds: number;
  sessionElapsedSeconds: number;
}

export interface CountdownResult {
  remainingSeconds: number;
  /** Which limit is closest, so the block can say why rather than just how long. */
  limitingFactor: LimitingFactor;
}

export const projectRemaining = (input: CountdownInput): CountdownResult => {
  const {
    serverRemainingSeconds,
    serverLimitingFactor,
    secondsSincePoll,
    isSessionRunning,
    budget,
    contentType,
    spentTodaySeconds,
    sessionElapsedSeconds,
  } = input;

  const advanced = isSessionRunning ? Math.max(secondsSincePoll, 0) : 0;

  const local = computeRemaining({
    budget,
    spent: {
      totalSeconds: spentTodaySeconds + advanced,
      // Not reported per type on the dashboard. The clamp below is what keeps
      // its absence from turning into an over-promise.
      byContentType: {},
    },
    contentType,
    sessionElapsedSeconds: sessionElapsedSeconds + advanced,
    // Likewise absent. The gate applies it; this screen defers to the gate.
    secondsUntilWindowEnd: null,
  });

  const serverProjected = serverRemainingSeconds - advanced;
  const remainingSeconds = Math.max(Math.min(local.remainingSeconds, serverProjected), 0);

  return {
    remainingSeconds,
    // Named by whichever side the figure came from. When the server's is the
    // smaller, it saw a limit this screen cannot — the schedule window, a type
    // budget — and the label has to say that one: "0 left, limit: daily budget"
    // at eleven at night, with half the budget unspent, was this screen naming
    // the wrong limit.
    limitingFactor:
      serverProjected < local.remainingSeconds ? serverLimitingFactor : local.limitingFactor,
  };
};
